import { CMSController, ControllerHelper, FileEntriesDownloadDto, PostController, PreviewDirectory, ProjectGroupDownloadDto, ProjectGroupUploadDto, ProjectStructureTemplateDownloadDto, UserShortInfoDownloadDto } from 'collaboration-service';
import DateTimeFns from 'components/DateTime/DateTimeFns';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import { Icon, Label, LoaderInline, Theme, Tooltip, belowBreakpointOrEqual, getUriFromLinkByName } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import 'react-tagsinput/react-tagsinput.css';
import { ApplicationState } from 'services/ApplicationState/ApplicationState';
import { calcMinsInHoursAndMins, shallowCompare } from 'services/Helpers';
import { getMediaLink } from 'services/Helpers/MediaHelpers';
import { sanitizedAllowedAttributesHighSafety, sanitizedAllowedTagsMediumSafety } from 'services/Helpers/SanitizedHelper';
import { getTranslated, getTranslatedExact, getTranslatedUserName } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { useThemePart } from 'services/useAppTheme';
import { ThemeContext } from 'styled-components';
import { cmsConfig } from './CMSConfig';
import { CMSSC as T } from './CMSSC';
import CMSTemplatePreview from './CMSTemplatePreview';
import ShowPreviewDirectory from 'components/ShowPreviewDirectory';

const mapper = (state: ApplicationState) => ({
    contentLanguage: state.contentLanguage,
    userGroups: state.userGroups,
    currentGroup: state.currentGroup,
});



export type RoleMap = Array<{ user: UserShortInfoDownloadDto, roles: string[] }>;
interface CMSProjectInfoProps {
    project?: ProjectGroupDownloadDto;
    templates?: ProjectStructureTemplateDownloadDto[];
    directories?: FileEntriesDownloadDto[];
    id?: string;
    admin: boolean;
    showBG?: boolean;
}

const CMSProjectInfo = (p: CMSProjectInfoProps) => {
    const { project: pProject, directories, id: projectId, showBG } = p;
    const emptyAvatarImageUrl = useThemePart(t => t.emptyAvatarImageUrl);
    const [files, setFiles] = React.useState<number>(0);

    const theme = React.useContext<Theme>(ThemeContext);
    const { contentLanguage, currentGroup } = useSelector(mapper, shallowCompare);
    const { t, currentLanguage } = useImgI18N("cms");
    const [roles, setRoles] = React.useState<RoleMap>();
    const lng = contentLanguage ?? currentLanguage;
    const [dto, setDto] = React.useState<ProjectGroupUploadDto>();
    const [structure, setStructure] = React.useState<ProjectStructureTemplateDownloadDto>();
    const [loadedProject, setloadedProject] = React.useState<ProjectGroupDownloadDto>();
    const [previewDirectory, setPreviewDirectory] = React.useState<PreviewDirectory>();
    const project = loadedProject ?? pProject;

    const groupId = currentGroup?.id ?? "";

    const id = project ? project.id : "";

    React.useEffect(() => {
        const l = async () => {
            if (projectId) {
                const pg = (await ControllerHelper.singleCall({ id: projectId }, PostController.GetSinglePost)) as ProjectGroupDownloadDto;
                setloadedProject(pg);

            }
        }
        l();
    }, [projectId])

    React.useEffect(() => {
        const getRoles = async (projectGroupId: string) => {
            const rolesRes = await ControllerHelper.singleCall({ projectGroupId }, CMSController.GetProjectGroupMembersWithRoles, true);
            if (rolesRes) {
                const roleMap = _.map(rolesRes.elements, e => ({ user: _.find(rolesRes.data, d => d.id === e.userId)!, roles: e.roles }));
                setRoles(roleMap);
            }
        };
        if (project) {
            setDto(project);
            setStructure((project as ProjectGroupDownloadDto).structureTemplate);
            getRoles(project.id);
        }
    }, [project, groupId]);

    React.useEffect(() => {
        const load = async () => {
            if (id) {
                const res = await ControllerHelper.singleCall({ byFileName: false, admin: false, feFilter: {}, filter: { onlyInProjectGroups: [id] }, lng: "en-GB", text: "*" },
                    CMSController.SearchFiles, true);
                setFiles(res?.totalCount ?? 0);
                const pv = await ControllerHelper.singleCall({ projectId: id }, CMSController.PreviewProjectOutput, true);
                setPreviewDirectory(pv);
            }
        };
        load();
    }, [id])

    const isMobile = belowBreakpointOrEqual({ theme }, "tablet");
    const preview = (project && getUriFromLinkByName(project.media, "self")) ?? theme.missingPreviewImageLink;

    if (!dto)
        return <LoaderInline active />;
    // console.log('CMSProjectInfo.tsx:86:dto => ', dto);
    return (
        <T.Container style={{
            paddingBottom: cmsConfig.showPreviewBackgroundImage ? 20 : 10,
            paddingRight: 10,
            position: cmsConfig.showPreviewBackgroundImage ? "relative" : "unset",
            marginBottom: cmsConfig.showPreviewBackgroundImage ? -22 : 0,
        }}>
            {cmsConfig.showPreviewBackgroundImage && showBG &&
                <T.ContainerBGImage url={preview} />
            }
            <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5 }}>
                <T.Label>
                    <Label icon="content aggregation" content={t("project name (id)")} bold />
                </T.Label>
                <T.Label>
                    {(getTranslatedExact(dto?.headlines, lng)?.text + " (#" + dto?.projectId + ")")}
                </T.Label>
            </T.Grid>
            <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5 }}>
                <T.Label>
                    <Label icon="hashtag" content={t("files count")} bold />
                </T.Label>
                <T.Label>
                    {files > 0 ? files : t("no files for this project available")}
                </T.Label>
            </T.Grid>
            {project &&
                <>
                    <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5 }}>
                        <T.Label>
                            <Label icon="calendar" content={t("created")} bold />
                        </T.Label>
                        <T.Label>
                            <div style={{ float: "left", marginRight: 5, marginTop: 2 }}>
                                <DateTimeFns
                                    date={project.created}
                                    dateFormat="PPPP"
                                    hideIcons
                                />
                            </div>
                            <span>- {getTranslatedUserName(project.creator)}</span>
                        </T.Label>
                    </T.Grid>
                    <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5 }}>
                        <T.Label>
                            <Label icon="calendar" content={t("changed")} bold />
                        </T.Label>
                        <T.Label>
                            <div style={{ float: "left", marginRight: 5, marginTop: 2 }}>
                                <DateTimeFns
                                    date={project.changed}
                                    dateFormat="PPPP"
                                    hideIcons
                                />
                            </div>
                            <span>- {getTranslatedUserName(project.changedBy)}</span>
                        </T.Label>
                    </T.Grid>
                </>
            }

            <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: 0 }}>
                <T.Label>
                    <Label icon="feed" content={t("description")} bold />
                </T.Label>
                <div style={{ paddingTop: 8 }}>
                    <SafeHTML
                        html={getTranslatedExact(dto?.descriptions, lng)?.text ?? ""}
                        allowedTags={sanitizedAllowedTagsMediumSafety}
                        allowedAttributes={sanitizedAllowedAttributesHighSafety}
                    />
                </div>
            </T.Grid>




            <T.GridBackgroundImage preview={preview} style={{ marginTop: -5, marginBottom: -5 }}>
                <T.Label>
                    <Label icon="folder plus" content={t("structure")} bold />
                </T.Label>
                <div style={{ marginLeft: -10 }}>
                    <CMSTemplatePreview template={structure} directories={directories} sidebarView={true} noBgColor />
                </div>
            </T.GridBackgroundImage>



            <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: 0 }}>
                <T.Label>
                    <Label icon="tag" content={t("categories")} bold />
                </T.Label>
                <T.TagInputContainer style={{ height: "unset", lineHeight: "unset", paddingTop: 7 }}>
                    {_.map(dto?.categories, c => {
                        return (
                            <T.TagItem key={c.id}>
                                <Icon name="tag" marginTop={2} size="12px" style={{ float: "left", marginRight: 2 }} />
                                {getTranslated(c.names).text}
                            </T.TagItem>
                        );
                    })}
                </T.TagInputContainer>
            </T.Grid>
            <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5 }}>
                <T.Label>
                    <Label icon="clock" content={t("training duration")} bold />
                </T.Label>
                <T.Label>
                    {calcMinsInHoursAndMins(dto.trainingDurationInMinutes, t)}
                </T.Label>
            </T.Grid>

            {project &&
                <>
                    <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5 }}>
                        <T.Label>
                            <Label icon="calendar" content={t("publication date")} bold />
                        </T.Label>
                        <T.Label>
                            <DateTimeFns
                                date={project.publicationDate!}
                                dateFormat="PPPP"
                                hideIcons
                                noTime
                            />
                        </T.Label>
                    </T.Grid>
                    <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5 }}>
                        <T.Label>
                            <Label icon="calendar" content={t("expiration date")} bold />
                        </T.Label>
                        <T.Label>
                            <DateTimeFns
                                date={project.expirationDate!}
                                dateFormat="PPPP"
                                hideIcons
                                noTime
                            />
                        </T.Label>
                    </T.Grid>
                </>
            }

            {roles && roles?.length > 0 &&
                <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: -5, marginTop: -5 }}>
                    <T.Label>
                        <Label icon="users" content={t("members")} bold />
                    </T.Label>
                    <div>
                        <LoaderInline active={roles === undefined} />
                        {_.map(roles, (r, j) => {
                            const isResp = _.find(project?.responsibleUsers, u => u.id === r.user.id) !== undefined;
                            return (
                                <T.ProjectRolesGrid
                                    key={j}
                                    style={{
                                        gridTemplateColumns: isMobile ? "30px 1fr" : "30px minmax(160px, max-content) 1fr",
                                        gridTemplateRows: "max-content",
                                        marginTop: isMobile ? -3 : 4,
                                        marginBottom: -3
                                    }}
                                >
                                    <div>
                                        <Tooltip
                                            tooltipText={isResp ? t("user is project responsible") : t("user is normal project member")}
                                            position="right"
                                        >
                                            <Icon name={isResp ? "circle dot" : "circle"} color={isResp ? "@accent" : undefined} />
                                        </Tooltip>
                                    </div>
                                    <T.RolesUser style={{ paddingLeft: 0, paddingRight: 10, lineHeight: "30px" }}>
                                        <T.RolesUserAvatar src={getMediaLink(r.user, u => u.avatar) ?? emptyAvatarImageUrl} />
                                        {getTranslatedUserName(r.user)}
                                    </T.RolesUser>
                                    <T.RolesRoles style={{
                                        marginTop: isMobile ? -5 : 0,
                                        gridColumnStart: 2,
                                        gridColumnEnd: isMobile ? 3 : 4,
                                        paddingLeft: isMobile ? 30 : 0
                                    }}
                                    >
                                        {_.map(r.roles, (role, i) => (
                                            <T.TagItemInfo key={i}>
                                                <Icon name="user" marginTop={0} size="12px" style={{ float: "left", marginRight: 2 }} />
                                                {role}
                                            </T.TagItemInfo>
                                        ))}
                                    </T.RolesRoles>
                                </T.ProjectRolesGrid>
                            )
                        }
                        )}
                    </div>
                </T.Grid>
            }
            <div style={{ height: 20 }} />

            {previewDirectory &&
                <T.Grid style={{ margin: 0, minHeight: "unset", marginBottom: 0 }}>
                    <T.Label>
                        <Label icon="file" content={t("available files")} bold />
                    </T.Label>
                    <ShowPreviewDirectory directory={previewDirectory} />
                </T.Grid>
            }

            <div style={{ height: 20 }} />
        </T.Container>
    );
}
export default CMSProjectInfo;